import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
} from '@backstage/theme';

// ARKANCE Brand Colors
const arkanceColors = {
  // Primary - Navy blue (header, footer, primary actions)
  navy: '#1B2B4B',
  navyDark: '#162236',
  navyLight: '#2A4066',

  // Secondary - Teal/Cyan (accents, highlights)
  teal: '#7df3e1',
  tealDark: '#5ed4c3',
  tealLight: '#a5f7eb',

  // Accent - Orange (category tags, alerts)
  orange: '#E8732C',
  orangeLight: '#F5A623',

  // Neutrals
  white: '#FFFFFF',
  grayLight: '#F5F7FA',
  gray: '#E2E8F0',
  grayMedium: '#94A3B8',
  grayDark: '#4A5568',
  black: '#1A202C',
};

// Light theme
export const arkanceLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: arkanceColors.navy,
      light: arkanceColors.navyLight,
      dark: arkanceColors.navyDark,
    },
    secondary: {
      main: arkanceColors.teal,
      light: arkanceColors.tealLight,
      dark: arkanceColors.tealDark,
    },
    error: {
      main: '#DC2626',
    },
    warning: {
      main: arkanceColors.orange,
    },
    success: {
      main: '#059669',
    },
    info: {
      main: arkanceColors.navy,
    },
    background: {
      default: arkanceColors.grayLight,
      paper: arkanceColors.white,
    },
    text: {
      primary: arkanceColors.navy,
      secondary: arkanceColors.grayDark,
    },
    navigation: {
      background: arkanceColors.navyDark,
      indicator: arkanceColors.teal,
      color: arkanceColors.white,
      selectedColor: arkanceColors.teal,
      navItem: {
        hoverBackground: arkanceColors.navyLight,
      },
      submenu: {
        background: arkanceColors.navy,
      },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.navy],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [arkanceColors.navy, arkanceColors.navyLight],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.tealDark],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [arkanceColors.navy, arkanceColors.teal],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.navyLight],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [arkanceColors.navy, arkanceColors.tealDark],
      shape: shapes.wave2,
    }),
    other: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.navy],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [arkanceColors.navy, arkanceColors.teal],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.navyLight],
      shape: shapes.wave2,
    }),
  },
  fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  components: {
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          backgroundColor: arkanceColors.navyDark,
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: arkanceColors.navyLight,
          },
        },
        selected: {
          backgroundColor: arkanceColors.navy,
          '&:hover': {
            backgroundColor: arkanceColors.navy,
          },
        },
        label: {
          color: arkanceColors.white,
        },
        iconContainer: {
          color: arkanceColors.white,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: `linear-gradient(135deg, ${arkanceColors.navyDark} 0%, ${arkanceColors.navy} 100%)`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '& a': {
            color: arkanceColors.teal,
            '&:hover': {
              color: arkanceColors.tealLight,
            },
          },
        },
        title: {
          color: arkanceColors.white,
        },
        subtitle: {
          color: arkanceColors.teal,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: arkanceColors.navyDark,
        },
        defaultTab: {
          color: 'rgba(255, 255, 255, 0.6)',
          '&.Mui-selected': {
            color: arkanceColors.white,
          },
          '&:hover': {
            color: arkanceColors.white,
            backgroundColor: arkanceColors.navyLight,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: arkanceColors.navy,
          '&:hover': {
            backgroundColor: arkanceColors.navyLight,
          },
        },
        containedSecondary: {
          backgroundColor: arkanceColors.teal,
          color: arkanceColors.navyDark,
          '&:hover': {
            backgroundColor: arkanceColors.tealDark,
          },
        },
        outlinedPrimary: {
          borderColor: arkanceColors.navy,
          color: arkanceColors.navy,
          '&:hover': {
            backgroundColor: 'rgba(27, 43, 75, 0.08)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
        },
        colorPrimary: {
          backgroundColor: arkanceColors.navy,
          color: arkanceColors.white,
        },
        colorSecondary: {
          backgroundColor: arkanceColors.teal,
          color: arkanceColors.navyDark,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: '8px',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: arkanceColors.navy,
          '&:hover': {
            color: arkanceColors.navyLight,
          },
        },
      },
    },
  },
});

// Dark theme
export const arkanceDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: arkanceColors.teal,
      light: arkanceColors.tealLight,
      dark: arkanceColors.tealDark,
    },
    secondary: {
      main: arkanceColors.teal,
      light: arkanceColors.tealLight,
      dark: arkanceColors.tealDark,
    },
    error: {
      main: '#EF4444',
    },
    warning: {
      main: arkanceColors.orangeLight,
    },
    success: {
      main: '#10B981',
    },
    info: {
      main: arkanceColors.teal,
    },
    background: {
      default: '#0F172A',
      paper: arkanceColors.navyDark,
    },
    text: {
      primary: arkanceColors.white,
      secondary: arkanceColors.grayMedium,
    },
    navigation: {
      background: '#0A1122',
      indicator: arkanceColors.teal,
      color: arkanceColors.white,
      selectedColor: arkanceColors.teal,
      navItem: {
        hoverBackground: arkanceColors.navyDark,
      },
      submenu: {
        background: '#0A1122',
      },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: ['#0A1122', arkanceColors.navyDark],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: ['#0A1122', arkanceColors.navy],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.tealDark],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: ['#0A1122', arkanceColors.tealDark],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#0A1122', arkanceColors.navyDark],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.tealDark],
      shape: shapes.wave2,
    }),
    other: genPageTheme({
      colors: ['#0A1122', arkanceColors.navyDark],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: ['#0A1122', arkanceColors.tealDark],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [arkanceColors.navyDark, arkanceColors.navy],
      shape: shapes.wave2,
    }),
  },
  fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  components: {
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          backgroundColor: '#0A1122',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: arkanceColors.navyDark,
          },
        },
        selected: {
          backgroundColor: arkanceColors.navy,
          '&:hover': {
            backgroundColor: arkanceColors.navy,
          },
        },
        label: {
          color: arkanceColors.white,
        },
        iconContainer: {
          color: arkanceColors.white,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: `linear-gradient(135deg, #0A1122 0%, ${arkanceColors.navyDark} 100%)`,
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        },
        title: {
          color: arkanceColors.white,
        },
        subtitle: {
          color: arkanceColors.teal,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: '#0A1122',
        },
        defaultTab: {
          color: 'rgba(255, 255, 255, 0.6)',
          '&.Mui-selected': {
            color: arkanceColors.white,
          },
          '&:hover': {
            color: arkanceColors.white,
            backgroundColor: arkanceColors.navyDark,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: arkanceColors.teal,
          color: arkanceColors.navyDark,
          '&:hover': {
            backgroundColor: arkanceColors.tealDark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: arkanceColors.navyDark,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: '8px',
        },
      },
    },
  },
});
